import { enemyClass, laserLifespan, asteroidRestitution, asteroidEnemyDamageSpeed, enemyAsteroidPush } from './constants';
import type { LaserImage } from './constants';
import { polygonMTV } from './util';
import type { Level } from './Level';

// Owns the enemy ships and their fire: spawning, the chase/fire AI, taking
// damage, and the collisions where the enemy is the one affected (a player laser
// hitting it, or a shot-in asteroid ramming it). The ship-side responses (the
// ship ramming an enemy, enemy fire hitting the ship) stay in Level. Shared
// helpers - the ship, laser removal, asteroid polygons, sfx volume - are reached
// through `level`.
export class Enemies {
    group: Phaser.Physics.Arcade.Group;
    fireGroup: Phaser.Physics.Arcade.Group;

    private polygonBase: Phaser.Geom.Point[];
    private sfx_fire: Phaser.Sound.BaseSound[] = [];
    private sfx_collide: Phaser.Sound.BaseSound;
    private sfx_explosion: Phaser.Sound.BaseSound;

    constructor(private level: Level) {
        this.group = level.physics.add.group({
            classType: Phaser.Physics.Arcade.Sprite,
            runChildUpdate: true
        });

        this.fireGroup = level.physics.add.group({
            classType: Phaser.Physics.Arcade.Image,
            maxSize: 30,
            runChildUpdate: true
        });

        // Enemy hull polygon - an arrow pointing +x (enemies rotate to face the
        // player). Approximate for the 80x80 sprite; refine with the D overlay.
        this.polygonBase = [
            new Phaser.Geom.Point(35, 0),     // nose (front)
            new Phaser.Geom.Point(5, -28),    // top front
            new Phaser.Geom.Point(-30, -22),  // top rear
            new Phaser.Geom.Point(-30, 22),   // bottom rear
            new Phaser.Geom.Point(5, 28)      // bottom front
        ];

        // Enemy-vs-enemy, resolved against the real hull polygons (SAT). Set up
        // once as a persistent overlap, the same way the asteroid pairs are.
        level.physics.add.overlap(this.group, this.group, this.enemyEnemyCollision, null, this);

        for (let i = 0; i < enemyClass.length; i++) {
            this.sfx_fire[i] = level.sound.add(enemyClass[i].FireSound);
        }
        this.sfx_collide = level.sound.add('enemycollide');
        this.sfx_explosion = level.sound.add('enemyexplosion');
    }

    setSfxVolume(volume: number) {
        (this.sfx_collide as any).setVolume(volume);
        (this.sfx_explosion as any).setVolume(volume);
        for (let i = 0; i < this.sfx_fire.length; i++) {
            if (this.sfx_fire[i]) {
                (this.sfx_fire[i] as any).setVolume(volume);
            }
        }
    }

    // The ship-vs-enemy ram (handled on the ship side) plays this on impact.
    playCollide() {
        this.sfx_collide.play();
    }

    spawn(x: number, y: number, kind: number): Phaser.Physics.Arcade.Sprite {
        const enemy = this.group.create(x, y, 'enemy' + kind) as Phaser.Physics.Arcade.Sprite;
        enemy.setData('kind', kind);  // Storing 'kind' as custom data

        // Create explosion animation for this enemy
        const explosionKey = 'explode-enemy' + kind;
        if (!this.level.anims.exists(explosionKey)) {
            this.level.anims.create({
                key: explosionKey,
                frames: this.level.anims.generateFrameNumbers('enemy' + kind, {
                    frames: enemyClass[kind].Explode
                }),
                frameRate: 15
            });
        }

        enemy.setOrigin(0.5, 0.5);
        enemy.setCollideWorldBounds(true);
        enemy.setBounce(1, 1);
        enemy.setData('shield', enemyClass[kind].Shield);
        enemy.setData('fireInterval', this.level.time.now + Phaser.Math.Between(0, 1000));
        // Enemies start idle and wake up when the player gets close - or when the
        // player shoots them, which wakes them for good (see isActive).
        enemy.setData('awake', false);

        return enemy;
    }

    // Get enemy polygon based on current position and rotation.
    // Enemies set .rotation directly (radians), so no degree conversion here.
    getEnemyPolygon(enemy: Phaser.Physics.Arcade.Sprite): Phaser.Geom.Polygon {
        const points: Phaser.Geom.Point[] = this.polygonBase.map(p => new Phaser.Geom.Point(p.x, p.y));

        const rotation = enemy.rotation;
        for (let i = 0; i < points.length; i++) {
            Phaser.Math.Rotate(points[i], rotation);
            points[i].x += enemy.x;
            points[i].y += enemy.y;
        }

        return new Phaser.Geom.Polygon(points);
    }

    update(time: number) {
        this.group.getChildren().forEach((enemy: Phaser.Physics.Arcade.Sprite) => {
            this.ai(enemy, time);
        });
    }

    ai(enemy: Phaser.Physics.Arcade.Sprite, time: number) {
        if (enemy.getData('shot')) {
            return;
        }

        const kind = enemy.getData('kind');
        const ec = enemyClass[kind];
        const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, this.level.ship.x, this.level.ship.y);

        if (this.isActive(enemy)) {
            // Enemy following
            let speed = (enemy.getData('shield') === 0) ? -ec.Speed : (distance > ec.FollowDistance ? ec.Speed : 0);
            this.level.physics.moveToObject(enemy, this.level.ship, speed);
            enemy.rotation = Phaser.Math.Angle.Between(enemy.x, enemy.y, this.level.ship.x, this.level.ship.y);

            // Enemy fire - only once the player is actually within view range. A
            // woken enemy hunts the player from any distance, but doesn't blaze
            // away from off-screen where its shots could never reach anyway.
            if (distance < ec.ViewDistance && time > enemy.getData('fireInterval')) {
                const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, this.level.ship.x, this.level.ship.y);
                const velocity = this.level.physics.velocityFromRotation(angle, ec.FireSpeed);

                const enemyFire = this.fireGroup.get(enemy.x, enemy.y, ec.FireKey) as LaserImage;
                if (enemyFire) {
                    enemyFire.setActive(true).setVisible(true);
                    enemyFire.rotation = angle;
                    enemyFire.body.velocity.copy(velocity);

                    this.level.sound.play(ec.FireSound, { volume: this.level.sfxVol() });

                    enemy.setData('fireInterval', time + ec.FireGap);

                    // Set a timer to deactivate the laser after laserLifespan
                    enemyFire.removeTimer = this.level.time.delayedCall(laserLifespan, () => {
                        if (enemyFire.active) {
                            this.level.removeLaser(enemyFire);
                        }
                    });
                }
            }
        } else {
            // Stop moving if out of range
            enemy.body.reset(enemy.x, enemy.y);
        }
    }

    // Apply one hit of damage to an enemy: drop its shield, or destroy it (with
    // explosion) once the shield is gone. Shared by laser hits and asteroid
    // impacts. No-op if the enemy is already exploding.
    damage(enemy: Phaser.Physics.Arcade.Sprite) {
        if (enemy.getData('shot')) {
            return;
        }
        if (enemy.getData('shield') <= 0) {
            enemy.setData('shot', true);
            enemy.play('explode-enemy' + enemy.getData('kind'));
            this.sfx_explosion.play();
            // Set a timer to remove the enemy after the explosion animation
            this.level.time.delayedCall(1000, () => {
                enemy.destroy();
            });
        } else {
            enemy.setData('shield', enemy.getData('shield') - 1);
        }
    }

    // Process callback for laser/enemy overlap: true only when the player's shot
    // actually touches the enemy ship's polygon, not just its bounding box.
    laserHitsEnemy(laser: Phaser.Physics.Arcade.Image, enemy: Phaser.Physics.Arcade.Sprite): boolean {
        return this.level.shotHitsPolygon(laser, this.getEnemyPolygon(enemy));
    }

    enemyShot(laser: Phaser.Physics.Arcade.Image, enemy: Phaser.Physics.Arcade.Sprite) {
        if (!enemy.getData('shot')) {
            // Being hit wakes an enemy for good, so it can't be picked off from
            // outside its view distance while it sits there idle.
            enemy.setData('awake', true);
            this.damage(enemy);
            // Deactivate the laser
            this.level.removeLaser(laser as LaserImage);
        }
    }

    // An enemy is "engaged" once the player has come within its view distance,
    // or once the player has shot it - a hit wakes it for good. Anything else is
    // idle: it sits still, and asteroids can't hurt it (see
    // asteroidEnemyCollision), so distant enemies no longer quietly destroy
    // themselves on drifting rocks before the player has even met them.
    isActive(enemy: Phaser.Physics.Arcade.Sprite): boolean {
        if (enemy.getData('awake')) {
            return true;
        }

        const ec = enemyClass[enemy.getData('kind')];
        const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, this.level.ship.x, this.level.ship.y);
        return distance < ec.ViewDistance;
    }

    // Two enemy ships bumping into each other. Their AI re-drives their velocity
    // every frame, so (as with the asteroid case) we resolve this by position:
    // push each ship half of the overlap apart along the contact normal. That
    // stops them sliding through one another without fighting the AI.
    enemyEnemyCollision(a: Phaser.Physics.Arcade.Sprite, b: Phaser.Physics.Arcade.Sprite) {
        if (a === b || a.getData('shot') || b.getData('shot')) {
            return; // same ship, or one of them is already exploding
        }

        const mtv = polygonMTV(this.getEnemyPolygon(a).points, this.getEnemyPolygon(b).points);
        if (!mtv) {
            return; // bounding boxes overlap but the hulls do not
        }

        // mtv normal points from a toward b, so back a off and push b away.
        const half = mtv.depth * 0.5;
        a.x -= mtv.nx * half;
        a.y -= mtv.ny * half;
        b.x += mtv.nx * half;
        b.y += mtv.ny * half;
    }

    // Overlap callback for an asteroid hitting an enemy ship. Uses the real rock
    // and ship polygons (SAT) to bounce the asteroid off and knock the enemy
    // aside, and damages the enemy on a solid impact - so shooting a rock into
    // an enemy is a real attack. (The enemy's AI re-drives its velocity every
    // frame, so we knock its position rather than its velocity.)
    asteroidEnemyCollision(asteroid: Phaser.Physics.Arcade.Image, enemy: Phaser.Physics.Arcade.Sprite) {
        if (enemy.getData('shot')) {
            return; // already exploding
        }

        const mtv = polygonMTV(this.level.getAsteroidPolygon(asteroid).points, this.getEnemyPolygon(enemy).points);
        if (!mtv) {
            return; // bounding boxes overlap but the shapes do not
        }

        // Separate them: push the asteroid back and shove the enemy a little.
        // (mtv normal points from the asteroid toward the enemy.)
        asteroid.x -= mtv.nx * mtv.depth * 0.6;
        asteroid.y -= mtv.ny * mtv.depth * 0.6;
        enemy.x += mtv.nx * mtv.depth * 0.4;
        enemy.y += mtv.ny * mtv.depth * 0.4;

        // Contact normal pointing from the enemy out towards the asteroid.
        const nx = -mtv.nx;
        const ny = -mtv.ny;

        const mass = asteroid.getData('mass') || 1;
        const av = asteroid.body.velocity;
        const ev = enemy.body.velocity;

        // Closing speed along the contact normal, measured BEFORE we change
        // anything - that is the real impact speed, used for damage below.
        const closing = -((av.x - ev.x) * nx + (av.y - ev.y) * ny);

        // 1. If the rock is travelling into the enemy, reflect its OWN velocity
        //    about the contact normal - the same "bounce off something solid"
        //    the player's ship gets, which preserves the rock's speed. Bouncing
        //    the rock's own velocity rather than its velocity RELATIVE to the
        //    enemy is the key fix: the old relative-velocity bounce treated the
        //    enemy as an infinitely heavy moving wall, handing the rock twice
        //    the enemy's speed on every contact. A rock that couldn't escape
        //    (pinned against the world bounds or another rock) was re-launched
        //    every cycle until it was flying absurdly fast - and then passed
        //    that speed on to every small rock it clipped.
        const rockAlongN = av.x * nx + av.y * ny;
        if (rockAlongN < 0) {
            av.x -= (1 + asteroidRestitution) * rockAlongN * nx;
            av.y -= (1 + asteroidRestitution) * rockAlongN * ny;
        }

        // 2. An enemy driving into a rock can still shove it along, but never
        //    faster than the enemy itself is moving - so it nudges rocks out of
        //    its way instead of pumping energy into them. Scaled by 1/mass, so a
        //    small rock is shifted readily and a big one barely budges.
        const enemyAlongN = ev.x * nx + ev.y * ny;
        const rockAfter = av.x * nx + av.y * ny;
        if (enemyAlongN > rockAfter) {
            const catchUp = (enemyAlongN - rockAfter) * Math.min(1, enemyAsteroidPush / mass);
            av.x += nx * catchUp;
            av.y += ny * catchUp;
        }

        // 3. A solid impact damages the enemy - but only once it is engaged, so
        //    idle enemies aren't killed by drifting rocks long before the player
        //    arrives. Rate-limited so a sustained grind doesn't drain the shield
        //    every frame.
        if (this.isActive(enemy)
            && closing > asteroidEnemyDamageSpeed
            && this.level.time.now > (enemy.getData('nextAsteroidHit') || 0)) {
            // Thud on a solid hit, but not on the killing blow - there the
            // explosion takes over, the same way the ship plays 'collide' when
            // it survives and 'explode' when it doesn't.
            if (enemy.getData('shield') > 0) {
                this.playCollide();
            }
            this.damage(enemy);
            enemy.setData('nextAsteroidHit', this.level.time.now + 500);
        }
    }
}
